import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { can, initials, type SearchResult } from '@repo/shared';
import { Icon, InitialsAvatar } from '@repo/ui';
import { useMe } from '../auth/session';
import { api } from '../lib/api';
import { navFor } from './nav';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

type Entry = { kind: 'student' | 'page'; id: string; go: () => void };

/** Ctrl/⌘ K global search: students (name, CIN, matricule) and pages the user may open. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label={t('palette.label')}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '12vh' } }}
      slotProps={{
        paper: {
          sx: {
            width: 600,
            maxWidth: 'calc(100vw - 32px)',
            m: 0,
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,.3)',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Children unmount when the dialog closes, so each opening starts with an empty query. */}
      <PaletteBody onClose={onClose} />
    </Dialog>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const listId = useId();
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 150);
  // Highlight resets to the first entry whenever the (debounced) query changes.
  const [cursor, setCursor] = useState({ q: '', i: 0 });
  const active = cursor.q === dq ? cursor.i : 0;
  const setActive = (update: (i: number) => number) => setCursor({ q: dq, i: update(active) });
  const canStudents = can(me.permissions, 'students');

  const search = useQuery({
    queryKey: ['search', dq],
    queryFn: () => api<SearchResult>(`/search?q=${encodeURIComponent(dq)}`),
    enabled: canStudents,
    placeholderData: (prev) => prev,
  });

  // Never show (or let Enter pick) results of a previous query while the new one is debounced/fetching.
  const stale = q.trim() !== dq || search.isPlaceholderData;
  const students = stale ? [] : (search.data?.students ?? []).slice(0, 5);
  const pages = useMemo(() => {
    const nq = normalize(q.trim());
    return navFor(me)
      .flatMap((g) => g.items)
      .filter((i) => !nq || normalize(t(`nav.items.${i.key}`)).includes(nq))
      .slice(0, 6);
  }, [me, q, t]);

  const go = (to: string) => {
    onClose();
    void navigate({ to });
  };
  const entries: Entry[] = [
    ...students.map((s) => ({ kind: 'student' as const, id: s.id, go: () => go(`/etudiants/${s.id}`) })),
    ...pages.map((p) => ({ kind: 'page' as const, id: p.key, go: () => go(p.to) })),
  ];

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(entries.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      entries[active]?.go();
    }
  };
  const optionId = (i: number) => `${listId}-opt-${i}`;
  const groupLabel = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: 'text.tertiary',
    px: 1.25,
    pt: 1,
    pb: 0.5,
  } as const;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          height: 56,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Icon name="search" size={22} sx={{ color: 'text.secondary' }} />
        <InputBase
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder={t('palette.placeholder')}
          inputProps={{
            role: 'combobox',
            'aria-expanded': true,
            'aria-controls': listId,
            'aria-activedescendant': entries.length ? optionId(active) : undefined,
            'aria-label': t('palette.label'),
          }}
          sx={{ flex: 1, fontSize: 16 }}
        />
        <Box
          component="span"
          sx={{
            height: 22,
            px: 0.75,
            borderRadius: '5px',
            border: 1,
            borderColor: 'outline',
            fontSize: 11,
            fontWeight: 600,
            color: 'text.tertiary',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {t('palette.escape')}
        </Box>
      </Box>
      <Box id={listId} role="listbox" sx={{ maxHeight: 400, overflow: 'auto', p: 1 }}>
        {canStudents && (
          <>
            <Box component="span" sx={groupLabel}>
              {t('palette.students')}
            </Box>
            {students.map((s, i) => (
              <ButtonBase
                key={s.id}
                id={optionId(i)}
                role="option"
                aria-selected={active === i}
                onClick={() => entries[i]?.go()}
                onMouseMove={() => setActive(() => i)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 48,
                  px: 1.25,
                  borderRadius: '8px',
                  justifyContent: 'flex-start',
                  textAlign: 'start',
                  bgcolor: active === i ? 'surface.container' : 'transparent',
                }}
              >
                <InitialsAvatar initials={initials(s.name)} size={30} />
                <Box
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column', lineHeight: '18px', minWidth: 0 }}
                >
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    {s.name}
                  </Box>
                  <Box component="span" sx={{ fontSize: 12, color: 'text.tertiary' }} className="tabular">
                    {t('palette.meta', { matricule: s.matricule, cin: s.cin ?? '—' })}
                    {s.levelCode
                      ? ` · ${t('palette.level', { level: s.levelCode, group: s.groupName ?? '—' })}`
                      : ''}
                  </Box>
                </Box>
                <Icon name="north_west" size={18} mirrorInRtl sx={{ color: 'text.tertiary' }} />
              </ButtonBase>
            ))}
            {!search.isFetching && dq && students.length === 0 && (
              <Box sx={{ px: 1.25, py: 2, color: 'text.tertiary', fontSize: 13 }}>
                {t('palette.noStudent', { q: dq })}
              </Box>
            )}
          </>
        )}
        {pages.length > 0 && (
          <Box component="span" sx={{ ...groupLabel, pt: 1.5 }}>
            {t('palette.pages')}
          </Box>
        )}
        {pages.map((p, j) => {
          const i = students.length + j;
          return (
            <ButtonBase
              key={p.key}
              id={optionId(i)}
              role="option"
              aria-selected={active === i}
              onClick={() => entries[i]?.go()}
              onMouseMove={() => setActive(() => i)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: 40,
                px: 1.25,
                borderRadius: '8px',
                justifyContent: 'flex-start',
                textAlign: 'start',
                bgcolor: active === i ? 'surface.container' : 'transparent',
              }}
            >
              <Icon name={p.icon} sx={{ color: 'text.secondary' }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t(`nav.items.${p.key}`)}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </>
  );
}
